import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Brain, Calendar, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent } from "@/platform/ui/card";
import { useSession } from "@/platform/auth/use-session";
import { gradeReview, loadDueReviews, type ReviewQuestion } from "@/tenants/einstein/features/review/api";
import { cn } from "@/platform/lib/cn";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

function ReviewShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="coral"
        className="pointer-events-none absolute -right-32 bottom-0 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />
      <Container size="default" className="relative py-16 md:py-24 animate-fade-up">
        {children}
      </Container>
    </div>
  );
}

const SELF_GRADES = [
  { value: 1, label: "Errei", description: "Não sabia ou chutei" },
  { value: 3, label: "Difícil", description: "Acertei com esforço" },
  { value: 4, label: "OK", description: "Confiante" },
  { value: 5, label: "Fácil", description: "Sabia de cor" },
] as const;

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  ingles: "Inglês",
  historia: "História",
  geografia: "Geografia",
};

type Stage = "loading" | "answering" | "feedback" | "done";

export function ReviewPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [stage, setStage] = useState<Stage>("loading");
  const [queue, setQueue] = useState<ReviewQuestion[]>([]);
  const [doneCount, setDoneCount] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const startedAtRef = useRef<number>(Date.now());

  const bootedRef = useRef(false);
  useEffect(() => {
    if (bootedRef.current) return;
    if (!session) return;
    bootedRef.current = true;
    (async () => {
      try {
        const rows = await loadDueReviews(session.user.id, 20);
        if (rows.length === 0) {
          setStage("done");
        } else {
          setQueue(rows);
          startedAtRef.current = Date.now();
          setStage("answering");
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Falha ao carregar revisões.");
        navigate("/painel", { replace: true });
      }
    })();
  }, [session, navigate]);

  const current = queue[0];

  function handleReveal() {
    if (!selected) {
      toast.error("Escolha uma alternativa antes de revelar.");
      return;
    }
    setRevealed(true);
    setStage("feedback");
  }

  const handleGrade = useCallback(
    async (grade: number) => {
      if (!current) return;
      try {
        await gradeReview(current.id, grade, {
          ease_factor: current.ease_factor,
          interval_days: current.interval_days,
          repetitions: current.repetitions,
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Falha ao gravar revisão.");
      }
      setQueue((q) => q.slice(1));
      setSelected(null);
      setRevealed(false);
      setDoneCount((c) => c + 1);
      setStage(queue.length === 1 ? "done" : "answering");
    },
    [current, queue.length],
  );

  if (stage === "loading") {
    return (
      <ReviewShell>
        <Badge variant="accentSolid">Revisão</Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold">
          Buscando questões pra revisar…
        </h1>
        <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
          Carregando fila SM-2 do seu perfil.
        </p>
      </ReviewShell>
    );
  }

  if (stage === "done") {
    return (
      <ReviewShell>
        <Badge variant={doneCount === 0 ? "outline" : "mint"}>
          {doneCount === 0 ? "Fila vazia" : "Sessão concluída"}
        </Badge>
        <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
          {doneCount === 0
            ? "Nada pra revisar agora."
            : `${doneCount} revisão${doneCount === 1 ? "" : "ões"} concluída${doneCount === 1 ? "" : "s"}.`}
        </h1>
        <p className="mt-3 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] text-pretty">
          {doneCount === 0
            ? "Sua fila SM-2 está vazia. Faça novas tentativas no diagnóstico ou nas discursivas pra alimentar a fila."
            : "Próximas revisões já agendadas pelo SM-2. Volte amanhã pra continuar consolidando."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button onClick={() => navigate("/plano")} size="lg" variant="gradient">
            Ver plano da semana
            <ArrowRight className="size-4" aria-hidden />
          </Button>
          <Button variant="outline" size="lg" onClick={() => navigate("/diagnostico/sessao")}>
            Fazer mais questões
          </Button>
        </div>
      </ReviewShell>
    );
  }

  if (!current) return null;
  const q = current.question;
  const isCorrect = q.correct_answer ? selected === q.correct_answer : null;

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Container size="default" className="relative py-12 md:py-16">
        <header className="flex items-center justify-between gap-4">
          <div>
            <Badge variant="accent">
              Revisão · {doneCount + 1} desta sessão · {queue.length} na fila
            </Badge>
            <h1 className="mt-3 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)]">
              {DISCIPLINE_LABELS[q.discipline] ?? q.discipline}
              {q.topic ? (
                <span className="ml-3 align-middle text-[length:var(--text-body)] font-normal text-[color:var(--color-muted-foreground)]">
                  · {q.topic}
                </span>
              ) : null}
            </h1>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-3 py-1.5 text-[length:var(--text-caption)] font-semibold shadow-[var(--shadow-xs)]">
            <Calendar className="size-3.5 text-[color:var(--color-accent)]" aria-hidden />
            Q{q.number ?? "?"}/{q.exam_year}
          </span>
        </header>

        <Card className="mt-8 p-7 md:p-8">
          <CardContent className="p-0 space-y-6">
            {q.has_base_text && q.base_text ? (
              <details className="rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5 text-[length:var(--text-body-sm)]">
                <summary className="cursor-pointer font-semibold text-[color:var(--color-foreground)]">
                  Texto-base · clique para ler
                </summary>
                <div className="mt-3 whitespace-pre-wrap text-[color:var(--color-muted-foreground)]">
                  {q.base_text}
                </div>
              </details>
            ) : null}

            <div className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed text-pretty">
              {q.stem}
            </div>

            {q.kind === "objective" && q.alternatives ? (
              <fieldset className="space-y-2.5">
                {q.alternatives.map((alt) => {
                  const isSelected = selected === alt.label;
                  const isRight = revealed && q.correct_answer === alt.label;
                  const isWrong = revealed && isSelected && q.correct_answer && q.correct_answer !== alt.label;
                  return (
                    <label
                      key={alt.label}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors duration-150 ease-out",
                        isRight
                          ? "border-[--color-mint] bg-[color:var(--color-mint-soft)]"
                          : isWrong
                            ? "border-[--color-pink] bg-[color:var(--color-pink-soft)]"
                            : isSelected
                              ? "border-[--color-accent] bg-[color:var(--color-accent-soft)]"
                              : "border-[--color-border] hover:border-[--color-accent]/40 hover:bg-[color:var(--color-paper)]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 grid size-6 place-items-center rounded-full text-[length:var(--text-caption)] font-bold shrink-0 transition-colors",
                          isRight
                            ? "bg-[color:var(--color-mint)] text-white"
                            : isWrong
                              ? "bg-[color:var(--color-pink)] text-white"
                              : isSelected
                                ? "bg-[color:var(--color-accent)] text-white"
                                : "bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)]",
                        )}
                      >
                        {alt.label}
                      </span>
                      <input
                        type="radio"
                        name="alternative"
                        value={alt.label}
                        checked={isSelected}
                        disabled={revealed}
                        onChange={() => setSelected(alt.label)}
                        className="sr-only"
                      />
                      <span className="flex-1 text-[length:var(--text-body-sm)] leading-relaxed">{alt.text}</span>
                      {isRight ? <CheckCircle2 className="size-5 text-[color:var(--color-mint-deep)] shrink-0" aria-hidden /> : null}
                      {isWrong ? <XCircle className="size-5 text-[color:var(--color-pink-deep)] shrink-0" aria-hidden /> : null}
                    </label>
                  );
                })}
              </fieldset>
            ) : (
              <p className="rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-4 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Discursiva: pense na resposta mentalmente, depois autoavalie quão bem você sabia.
              </p>
            )}
          </CardContent>
        </Card>

        {revealed && q.correct_answer ? (
          <div
            className={cn(
              "mt-6 rounded-xl border-2 p-5 shadow-[var(--shadow-sm)]",
              isCorrect
                ? "border-[--color-mint] bg-[color:var(--color-mint-soft)]"
                : "border-[--color-pink] bg-[color:var(--color-pink-soft)]",
            )}
          >
            <p className="flex items-center gap-2 font-display text-[length:var(--text-h4)] font-semibold">
              {isCorrect ? (
                <CheckCircle2 className="size-5 text-[color:var(--color-mint-deep)]" aria-hidden />
              ) : (
                <XCircle className="size-5 text-[color:var(--color-pink-deep)]" aria-hidden />
              )}
              {isCorrect ? "Acertou." : `Resposta correta: ${q.correct_answer}`}
            </p>
          </div>
        ) : null}

        <div className="mt-8 flex items-center justify-between gap-4">
          <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            {revealed ? "Quão bem você sabia?" : "Marque sua resposta e revele."}
          </p>
          {!revealed ? (
            <Button size="xl" onClick={handleReveal} disabled={q.kind === "objective" && !selected}>
              Revelar resposta
              <ArrowRight className="size-4" aria-hidden />
            </Button>
          ) : (
            <div className="flex flex-wrap gap-2">
              {SELF_GRADES.map((g) => (
                <Button
                  key={g.value}
                  variant={g.value <= 2 ? "secondary" : "primary"}
                  size="md"
                  onClick={() => handleGrade(g.value)}
                >
                  <Brain className="size-3.5" aria-hidden />
                  {g.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {revealed ? (
          <p className="mt-3 text-right text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            Errei · 1d &nbsp;|&nbsp; Difícil · 1d &nbsp;|&nbsp; OK ·{" "}
            {Math.max(1, Math.round(current.interval_days * current.ease_factor))}d &nbsp;|&nbsp; Fácil ·{" "}
            {Math.max(1, Math.round(current.interval_days * (current.ease_factor + 0.15)))}d
          </p>
        ) : null}
      </Container>
    </div>
  );
}
